import {
  Bucket,
  marker,
  MarkerMode,
  MarkerModeName,
  MarkerPropName,
} from "@cartographerio/atlas-map";
import { checkExhausted } from "@cartographerio/util";
import {
  expr,
  MapboxArg,
  MapboxCircleLayout,
  MapboxCirclePaint,
  MapboxExpr,
  MapboxFillLayout,
  MapboxFillPaint,
  MapboxLineLayout,
  MapboxLinePaint,
} from "../mapbox";

export function bucketFilterExpr(
  bucket: Bucket,
  valueExpr: MapboxArg
): MapboxExpr {
  switch (bucket.type) {
    case "LookupBucket": {
      return expr.eql(valueExpr, bucket.value ?? null);
    }

    case "RangeBucket": {
      const minBound: MapboxArg =
        bucket.minValue == null ? true : expr.gte(valueExpr, bucket.minValue);

      const maxBound: MapboxArg =
        bucket.maxValue == null
          ? true
          : bucket.maxInclusive
          ? expr.lte(valueExpr, bucket.maxValue)
          : expr.lt(valueExpr, bucket.maxValue);

      return expr.all(minBound, maxBound);
    }

    default:
      return checkExhausted(bucket);
  }
}

function styleExpr<A extends MarkerPropName>(
  getExpr: MapboxExpr,
  buckets: Bucket[],
  styleKey: A,
  modeKey: MarkerModeName
): MapboxExpr | MarkerMode[A] {
  switch (buckets.length) {
    case 0:
      return marker.standard[modeKey][styleKey];

    case 1:
      return buckets[0].marker[modeKey][styleKey];

    default:
      // Mapbox "match" and "step" expressions don't allow you to match on `null`
      // so we use a "case" expression here (expr.choose() in the code).
      // The type definitions still disallow nulls (hence the cast) but this seems to work.
      return expr.choose({
        cases: [
          ...buckets.map<[MapboxExpr, MarkerMode[A]]>(bucket => [
            bucketFilterExpr(bucket, getExpr),
            bucket.marker[modeKey][styleKey],
          ]),
        ],
        fallback: marker.empty[modeKey][styleKey],
      });
  }
}

export function circlePaintExpr(
  getExpr: MapboxExpr,
  buckets: Bucket[],
  modeKey: MarkerModeName
): MapboxCirclePaint {
  const size = styleExpr(getExpr, buckets, "size", modeKey);
  const fillColor = styleExpr(getExpr, buckets, "fillColor", modeKey);
  const strokeColor = styleExpr(getExpr, buckets, "strokeColor", modeKey);
  const strokeWidth = styleExpr(getExpr, buckets, "strokeWidth", modeKey);

  const ans: MapboxCirclePaint = {
    "circle-radius": expr.interpolate({
      type: ["linear"],
      input: expr.zoom(),
      stops: [
        [4, expr.mul(4, size)],
        [16, expr.mul(8, size)],
      ],
    }),
    "circle-color": fillColor,
    "circle-stroke-width": expr.interpolate({
      type: ["linear"],
      input: expr.zoom(),
      stops: [
        [4, expr.mul(1, strokeWidth)],
        [16, expr.mul(3, strokeWidth)],
      ],
    }),
    "circle-stroke-color": strokeColor,
    "circle-pitch-alignment": "map",
  };

  // console.log("CIRCLE PAINT", ans);

  return ans;
}

export function circleLayoutExpr(
  _getExpr: MapboxExpr,
  _buckets: Bucket[],
  _modeKey: MarkerModeName
): MapboxCircleLayout {
  return {};
}

export function linePaintExpr(
  getExpr: MapboxExpr,
  buckets: Bucket[],
  modeKey: MarkerModeName
): MapboxLinePaint {
  const lineWidth = styleExpr(getExpr, buckets, "strokeWidth", modeKey);
  const lineColor = styleExpr(getExpr, buckets, "fillColor", modeKey);

  const ans: MapboxLinePaint = {
    "line-width": expr.interpolate({
      type: ["linear"],
      input: expr.zoom(),
      stops: [
        [4, expr.mul(1, lineWidth)],
        [16, expr.mul(3, lineWidth)],
      ],
    }),
    "line-color": lineColor,
  };

  // console.log("LINE PAINT", ans);

  return ans;
}

export function lineLayoutExpr(
  _getExpr: MapboxExpr,
  _buckets: Bucket[],
  _modeKey: MarkerModeName
): MapboxLineLayout {
  return {};
}

export function fillPaintExpr(
  getExpr: MapboxExpr,
  buckets: Bucket[],
  modeKey: MarkerModeName
): MapboxFillPaint {
  return {
    "fill-color": styleExpr(getExpr, buckets, "fillColor", modeKey),
  };
}

export function fillLayoutExpr(
  _getExpr: MapboxExpr,
  _buckets: Bucket[],
  _modeKey: MarkerModeName
): MapboxFillLayout {
  return {};
}
